import { useId, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { PublicSourceAnalyticsSchedule, SourceScheduleInput } from "../../../../shared/types";
import { useSurfaces } from "../../hooks/useSurfaces";
import { api } from "../../lib/api";
import { defaultSourceSchedule } from "../../lib/source-schedule-defaults";
import { Button } from "../ui/Button";
import { Input } from "../ui/Input";
import { ScheduleFields } from "../ui/ScheduleFields";

export function SourceHistoryImport({ source, brandId }: { source: PublicSourceAnalyticsSchedule; brandId: string }) {
  const id = useId();
  const history = source.history!;
  const client = useQueryClient();
  // Closed by default: for a new source this is the alternative to "Turn on daily refresh".
  const [open, setOpen] = useState(false);
  const isYouTube = source.capability === "google.youtube.analytics.read";
  const surfaceKind = isYouTube ? "youtube" : "website";
  const surfaceLabel = isYouTube ? "channel/YouTube surface" : "Website";
  const surfaces = useSurfaces(open && !history.pending ? brandId : undefined, { kind: surfaceKind, status: "active" });
  const eligibleSurfaces = (surfaces.data?.data ?? []).filter(surface => surface.brand_id === brandId
    && surface.kind === surfaceKind && surface.status === "active" && surface.control_level === "owned");
  const [surface, setSurface] = useState(source.surface_id ?? "");
  const selectedSurface = eligibleSurfaces.some(candidate => candidate.id === surface) ? surface : "";
  const [start, setStart] = useState(history.start);
  const [end, setEnd] = useState(history.end);
  const [schedule, setSchedule] = useState<SourceScheduleInput>(source.schedule?.settings
    ?? defaultSourceSchedule());
  const [revision, setRevision] = useState(source.schedule?.revision);
  const invalidateSource = async () => { await Promise.all([
    client.invalidateQueries({ queryKey: ["source-schedules", brandId] }),
    client.invalidateQueries({ queryKey: ["analytics-refresh", brandId] }),
    client.invalidateQueries({ queryKey: ["brand-analytics-properties", brandId] }),
    client.invalidateQueries({ queryKey: ["connection-resource-assignments", source.connection_id] }),
  ]); };
  const savePairing = async () => {
    if (!selectedSurface || surfaces.isError) throw new Error(`Choose an active owned ${surfaceLabel} for this source.`);
    if (!source.resource_id) throw new Error(`Reload this source before changing its ${surfaceLabel}.`);
    await api.patch(`/api/connections/${encodeURIComponent(source.connection_id)}/resources/${encodeURIComponent(source.resource_id)}/assignments/${encodeURIComponent(source.assignment_id)}`,
      { target_surface_id: selectedSurface, reason: `Owner selected ${surfaceLabel} during analytics setup` });
  };
  const pairing = useMutation({ mutationFn: savePairing, onSettled: invalidateSource });
  const mutation = useMutation({
    mutationFn: async () => {
      if (!selectedSurface || surfaces.isError) throw new Error(`Choose an active owned ${surfaceLabel} for this source.`);
      if (selectedSurface !== source.surface_id) {
        await savePairing();
      }
      return api.post(`/api/brands/${encodeURIComponent(brandId)}/analytics/schedules/${encodeURIComponent(source.assignment_id)}`,
        { capability: source.capability, schedule, expected_revision: revision, import: { start, end } });
    },
    onSuccess: () => setOpen(false),
    onSettled: invalidateSource,
  });
  if (history.pending) return <p role="status" className="mt-3 text-xs text-slate-600">
    History import {history.requested_start} – {history.requested_end} is incomplete. {source.schedule?.enabled
      ? "Eligible report work resumes on scheduled checks." : "Enable automatic refresh to resume."} Finish this range before requesting older history.
  </p>;
  return <div className="mt-4 space-y-3">
    {!open && <Button size="sm" variant="secondary" onClick={() => { setRevision(source.schedule?.revision); setOpen(true); }}>{source.schedule ? "Import older history" : "Import history and turn on refresh"}</Button>}
    {open && <form className="space-y-3" onSubmit={event => { event.preventDefault(); mutation.mutate(); }}>
      <h4 className="text-sm font-medium">{source.schedule ? "Import history" : "Import history and turn on refresh"}</h4>
      <p className="text-xs text-slate-600">{source.schedule ? "Choose the history to import." : `Start with up to ${Math.min(90, history.maximum_days)} available days.`} Recent processing days are excluded; provider retention and access may limit results. You can import older dates afterward.</p>
      <div className="space-y-2">
        <label htmlFor={`${id}-surface`} className="text-sm font-medium">{surfaceLabel} for this source</label>
        <select id={`${id}-surface`} required value={selectedSurface} onChange={event => { setSurface(event.target.value); pairing.reset(); }}
          disabled={mutation.isPending || pairing.isPending || surfaces.isLoading}
          className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500">
          <option value="">Choose {surfaceLabel}</option>
          {eligibleSurfaces.map(candidate => <option key={candidate.id} value={candidate.id}>{candidate.display_name} · {candidate.canonical_url}</option>)}
        </select>
        {selectedSurface && selectedSurface !== source.surface_id && <Button size="sm" type="button" variant="secondary"
          loading={pairing.isPending} disabled={mutation.isPending || surfaces.isLoading || surfaces.isError}
          onClick={() => pairing.mutate()}>Save {isYouTube ? "channel" : "website"}</Button>}
        {pairing.isSuccess && <p role="status" className="text-xs text-slate-600">Pairing saved. Collection and refresh settings are unchanged.</p>}
        {pairing.error && <p role="alert" className="text-xs text-red-700">{pairing.error.message} Pairing was not confirmed; review the reloaded source before retrying.</p>}
        {surfaces.isError && <p role="alert" className="text-xs text-red-700">Unable to load {surfaceLabel}s. Reload before setting up analytics.</p>}
      </div>
      <div className="grid gap-3 sm:grid-cols-2">
        <Input id={`${id}-start`} label="Import from" type="date" required min={history.earliest} max={end}
          value={start} onChange={event => setStart(event.target.value)} disabled={mutation.isPending} />
        <Input id={`${id}-end`} label="Import through" type="date" required min={start} max={history.end}
          value={end} onChange={event => setEnd(event.target.value)} disabled={mutation.isPending} />
      </div>
      {!source.schedule && <ScheduleFields value={schedule} onChange={setSchedule} disabled={mutation.isPending} />}
      <p className="text-xs text-slate-600">Confirming authorizes this brand to import {start} – {end}, including dates before assignment, and enables automatic refresh. Imports resume in batches; ordinary refresh will not restart completed history. Earliest available request: {history.earliest}.</p>
      <Button size="sm" type="submit" loading={mutation.isPending}
        disabled={pairing.isPending || source.blockers.some(reason => reason !== "enrollment_incomplete") || !selectedSurface || surfaces.isLoading || surfaces.isError}>
        {source.schedule ? "Authorize import and enable refresh" : "Authorize import and set up refresh"}
      </Button>
      <Button size="sm" type="button" variant="ghost" disabled={mutation.isPending}
        onClick={() => { setOpen(false); mutation.reset(); }}>Cancel import</Button>
    </form>}
    {mutation.isPending && <p role="status" className="text-xs text-slate-600">Saving import and reloading status…</p>}
    {mutation.error && <div className="space-y-2">
      <p role="alert" className="text-xs text-red-700">{mutation.error.message} Setup was not confirmed; review reloaded status before retrying.</p>
      {source.schedule && <Button size="sm" variant="secondary" onClick={() => {
        setRevision(source.schedule?.revision);
        if (source.schedule?.settings) setSchedule(source.schedule.settings);
        mutation.reset();
      }}>Review reloaded settings</Button>}
    </div>}
  </div>;
}
